
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CampaignDetails } from "./CampaignFormSections/CampaignDetails";
import { VoucherDetails } from "./CampaignFormSections/VoucherDetails";
import { LocationSelector } from "./CampaignFormSections/LocationSelector";
import { VoucherPreviewDialog } from "./CampaignFormSections/VoucherPreviewDialog";
import { campaignFormSchema } from "./CampaignFormSections/schema";

export type CampaignFormValues = z.infer<typeof campaignFormSchema>;

const defaultValues: Partial<CampaignFormValues> = {
  title: "",
  description: "",
  campaignType: "unique",
  voucherCount: 100,
  startDate: new Date(),
  endDate: new Date(),
  voucherDetails: "",
  sponsorName: "",
  sponsorMessage: "",
  venues: [],
  isDraft: false,
};

type CampaignFormProps = {
  onSubmit: (values: CampaignFormValues) => void;
  initialValues?: Partial<CampaignFormValues>;
  isEditMode?: boolean;
};

export function CampaignForm({ onSubmit, initialValues = {}, isEditMode = false }: CampaignFormProps) {
  const [imagePreview, setImagePreview] = useState<string | null>(initialValues.voucherImage || null);
  
  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: { ...defaultValues, ...initialValues },
  });

  const handleSubmit = (values: CampaignFormValues) => {
    if (values.endDate < values.startDate) {
      form.setError("endDate", {
        type: "manual",
        message: "End date must be after start date",
      });
      return;
    }
    
    toast.success(isEditMode ? "Campaign updated" : "Campaign created");
    onSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <div className="grid gap-8 md:grid-cols-2">
          <CampaignDetails form={form} />
          <VoucherDetails 
            form={form} 
            imagePreview={imagePreview} 
            setImagePreview={setImagePreview} 
          />
        </div>

        <Separator />

        <LocationSelector form={form} />

        <div className="flex justify-end space-x-4">
          <VoucherPreviewDialog 
            formValues={form.getValues()} 
            imagePreview={imagePreview} 
          />
          <Button variant="outline" type="button">
            Cancel
          </Button>
          <Button type="submit">
            {isEditMode ? "Update Campaign" : "Create Campaign"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
